/**
 * PullRequestList - Display open pull requests with pagination.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { GitPullRequest, ExternalLink, Plus, Minus, Loader2 } from "lucide-react";

interface PullRequest {
  number: number;
  title: string;
  state: string;
  draft: boolean;
  headRef: string;
  baseRef: string;
  userLogin: string;
  url: string;
  additions: number;
  deletions: number;
}

interface PullRequestListProps {
  pullRequests: PullRequest[];
  isLoading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

export function PullRequestList({ pullRequests, isLoading, hasMore, onLoadMore }: PullRequestListProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitPullRequest className="w-4 h-4" />
          Open Pull Requests ({pullRequests.length}{hasMore ? "+" : ""})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[240px]">
          {pullRequests.length === 0 && !isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No open pull requests
            </p>
          ) : (
            <div className="space-y-2 pr-4">
              {pullRequests.map((pr) => (
                <a
                  key={pr.number}
                  href={pr.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2 rounded-md hover:bg-muted transition-colors group"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-sm text-muted-foreground flex-shrink-0">
                      #{pr.number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm truncate flex-1">{pr.title}</p>
                        {pr.draft && (
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            draft
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>@{pr.userLogin}</span>
                        <span className="text-green-600 flex items-center gap-0.5">
                          <Plus className="w-3 h-3" />
                          {pr.additions}
                        </span>
                        <span className="text-red-600 flex items-center gap-0.5">
                          <Minus className="w-3 h-3" />
                          {pr.deletions}
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                </a>
              ))}
              {hasMore && onLoadMore && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={onLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
